import { showErrorPopup, translateError } from "@components/ErrorHandling";
import {
  fetchData,
  applySortCouponsRules,
  filterGameRewardCatalogue,
  withFilteredCatalogue,
  withFilteredCoupons,
} from "../../Helpers";
import DOMPurify from "dompurify";

export const COUPON_STALE_TIME = 1000 * 60;
export const COUPON_CACHE_TIME = Infinity;

export async function getCouponCatalogueStandardList(
  payload = {
    limit: 100,
    offset: 0,
    // expired: false,
  },
) {
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/catalogue/standard/list",
    payload,
    successCallback: (resObj) => {
      dataLayer.push({
        event: "view_catalogue_list",
        ...resObj.data,
      });
    },
    controller: controller,
  });

  const sortedCatalouge = applySortCouponsRules(
    filterGameRewardCatalogue(result?.catalogue),
  );

  return {
    ...result,
    catalogue: sortedCatalouge,
    count: sortedCatalouge.length,
  };
}

export async function getCouponCatalogueLimitedList(
  payload = {
    limit: 100,
    offset: 0,
  },
) {
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/catalogue/limited/list",
    payload,
    successCallback: () => {},
    controller: controller,
  });

  return withFilteredCatalogue(result);
}

export async function getCouponCatalogueUltraInsiderList(
  payload = {
    limit: 100,
    offset: 0,
    // device: ["web"],
  },
) {
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/catalogue/ultrainsider/list",
    payload,
    successCallback: () => {},
    controller: controller,
  });

  return withFilteredCatalogue(result);
}

export async function getCouponList(
  payload = {
    limit: 100,
    offset: 0,
    expired: false,
  },
  options = {},
) {
  const { includeGameRewards = false } = options;
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/list",
    payload,
    successCallback: (resObj) => {
      dataLayer.push({
        event: "view_rewards",
        ...resObj.data,
      });
    },
    controller: controller,
  });

  return includeGameRewards ? result : withFilteredCoupons(result);
}

export function getCouponCatalogueDescribe(ident) {
  const controller = new AbortController();

  return fetchData({
    endpoint: "/coupon/catalogue/describe",
    payload: {
      catalogue: {
        ident: DOMPurify.sanitize(ident),
      },
    },
    successCallback: (resObj) => {
      dataLayer.push({
        event: "select_catalogue",
        ...resObj.data,
      });
    },
    errorCallback: (resObj) => {
      const { error } = resObj.data;
      const { id, ts } = resObj.meta;

      const specialErrors = {
        error_empty_coupon_database: "error_empty_coupon_database_auction",
        error_limit_exceeded: "error_limit_exceeded_auction",
        error_periodic_limit_exceeded: "error_periodic_limit_exceeded_auction",
      };

      const translatedError =
        ident === "box_tokyo_hotel" && specialErrors[error]
          ? specialErrors[error]
          : error;

      if (error === "error_catalogue_not_found") {
        return;
      }

      showErrorPopup(translateError(translatedError, id), ts);
    },
    shouldThrowError: false,
    controller: controller,
  });
}

export function getCouponCatalogueDescribeUltraInsider(ident) {
  const controller = new AbortController();

  return fetchData({
    endpoint: "/coupon/catalogue/ultrainsider/describe",
    payload: {
      catalogue: {
        ident: DOMPurify.sanitize(ident),
      },
      device: ["web"],
    },
    successCallback: (resObj) => {
      dataLayer.push({
        event: "select_catalogue",
        ...resObj.data,
      });
    },
    errorCallback: (resObj) => {
      const { error } = resObj.data;
      const { id, ts } = resObj.meta;

      const specialErrors = {
        error_empty_coupon_database: "error_empty_coupon_database_auction",
        error_limit_exceeded: "error_limit_exceeded_auction",
        error_periodic_limit_exceeded: "error_periodic_limit_exceeded_auction",
      };

      const translatedError =
        ident === "box_tokyo_hotel" && specialErrors[error]
          ? specialErrors[error]
          : error;

      if (error === "error_catalogue_not_found") {
        return;
      }

      showErrorPopup(translateError(translatedError, id), ts);
    },
    shouldThrowError: false,
    controller: controller,
  });
}

export function getCouponCatalogueDescribeAuction(
  ident,
  errorCallback,
  successCallback,
) {
  const controller = new AbortController();
  return fetchData({
    endpoint: "/coupon/catalogue/describe",
    payload: {
      catalogue: {
        ident: DOMPurify.sanitize(ident),
      },
    },
    successCallback: (resObj) => {
      successCallback(resObj);
    },
    errorCallback: (resObj) => {
      errorCallback(resObj);
    },
    shouldThrowError: false,
    controller: controller,
  });
}

// export function getPrizeCatalogueDescribe(ident) {
//   const controller = new AbortController();
//   return fetchData({
//     endpoint: "/prize/catalogue/describe",
//     payload: {
//       catalogue: {
//         ident: DOMPurify.sanitize(ident),
//       },
//     },
//     successCallback: () => {},
//     controller: controller,
//   });
// }

// export function getPrizeList(
//   payload = {
//     limit: 100,
//     offset: 0,
//     expired: false,
//   }
// ) {
//   const controller = new AbortController();
//   return fetchData({
//     endpoint: "/prize/list",
//     payload,
//     successCallback: () => {},
//     controller: controller,
//   });
// }

export function getCouponDescribe(uuid) {
  const controller = new AbortController();
  return fetchData({
    endpoint: "/coupon/describe",
    payload: {
      coupon: {
        uuid: uuid,
      },
    },
    successCallback: () => {},
    controller: controller,
  });
}

export async function getCouponServe(uuid) {
  const tokenUUID =
    new URLSearchParams(window.location.search).get("token") ||
    localStorage.getItem("token") ||
    "";

  const response = await fetch(
    import.meta.env.VITE_API_URL + "/coupon/serve",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${tokenUUID}`,
      },
      body: JSON.stringify({
        coupon: {
          uuid: uuid,
        },
      }),
    },
  );

  const blob = await response.blob();

  const disposition = response.headers.get("content-disposition") ?? "";
  const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
  const filename = match ? decodeURIComponent(match[1]) : null;

  return { blob, filename };
}

export async function getCouponCatalogueFavouriteList(
  payload = {
    limit: 100,
    offset: 0,
    // expired: false,
  },
) {
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/catalogue/favourite/list",
    payload: payload,
    successCallback: (resObj) => {
      dataLayer.push({
        event: "view_catalogue_favourites_list",
        ...resObj.data,
      });
    },
    controller: controller,
  });

  return withFilteredCatalogue(result);
}

export async function getCouponStoresList(
  payload = {
    limit: 10,
    offset: 0,
    // expired: false,
  },
) {
  const controller = new AbortController();
  const result = await fetchData({
    endpoint: "/coupon/catalogue/store/list",
    payload: payload,
    successCallback: () => {},
    controller: controller,
  });

  return withFilteredCatalogue(result);
}

// export function getPrizeDescribe(uuid) {
//   const controller = new AbortController();
//   return fetchData({
//     endpoint: "/prize/describe",
//     payload: {
//       prize: {
//         uuid: DOMPurify.sanitize(uuid),
//       },
//     },
//     successCallback: () => {},
//     controller: controller,
//   });
// }
